import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Ruler, CircleDot } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { PatternLayer, patternLight } from "@/components/landing/patterns";
import { useSEO } from "@/lib/seo";

// US size → [inner diameter mm, inner circumference mm]
const SIZES = [
  [4, 14.9, 46.8], [4.5, 15.3, 48.0], [5, 15.7, 49.3], [5.5, 16.1, 50.6],
  [6, 16.5, 51.9], [6.5, 16.9, 53.1], [7, 17.3, 54.4], [7.5, 17.7, 55.7],
  [8, 18.1, 57.0], [8.5, 18.6, 58.3], [9, 19.0, 59.5], [9.5, 19.4, 60.8],
  [10, 19.8, 62.1], [10.5, 20.2, 63.4], [11, 20.6, 64.6], [11.5, 21.0, 65.9],
  [12, 21.4, 67.2],
];

const MM = 7; // pixels per mm for the illustration

const pretty = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

export default function RingSizeGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useSEO({
    title: "Ring Size Guide | Gold Time Jewelry",
    canonicalPath: "/ring-size-guide",
    description: "Find your ring size with Gold Time Jewelry's guide — stock sizes, resizing options and how sizing works before your ring ships.",
  });

  return (
    <main data-testid="ring-guide-page">
      <Header />
      <section className="relative overflow-hidden pt-[164px] pb-24 lg:pt-[196px] lg:pb-32">
        <PatternLayer pattern={patternLight} />
        <div className="relative max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <Eyebrow>Find your fit</Eyebrow>
            <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight text-neutral-900" data-testid="ring-guide-heading">
              Ring Size <span className="text-[#C9A227]">Guide</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-neutral-600 leading-relaxed max-w-2xl">
              Two easy ways to find your size at home — and remember, every ring we sell can be sized
              before it ships.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-14 grid md:grid-cols-2 gap-6">
              <div className="bg-white border border-[#E8DCC4] rounded-sm p-7" data-testid="method-ring">
                <CircleDot size={26} strokeWidth={1.25} className="text-[#C9A227]" />
                <h2 className="font-display text-xl text-neutral-900 mt-4">Measure a ring you already own</h2>
                <ol className="mt-4 space-y-3 text-sm text-neutral-600 leading-relaxed list-decimal pl-5">
                  <li>Pick a ring that fits the finger you're shopping for.</li>
                  <li>Lay it flat and measure straight across the <em>inside</em> edge — the inner diameter — in millimeters.</li>
                  <li>Match that measurement to the chart below. Between two sizes? Go up a half size.</li>
                </ol>
              </div>
              <div className="bg-white border border-[#E8DCC4] rounded-sm p-7" data-testid="method-string">
                <Ruler size={26} strokeWidth={1.25} className="text-[#C9A227]" />
                <h2 className="font-display text-xl text-neutral-900 mt-4">Measure your finger</h2>
                <ol className="mt-4 space-y-3 text-sm text-neutral-600 leading-relaxed list-decimal pl-5">
                  <li>Wrap a thin strip of paper or string snugly around the base of your finger.</li>
                  <li>Mark where it overlaps, then measure the length in millimeters — that's your circumference.</li>
                  <li>Match it to the chart below. Measure at the end of the day, when fingers are slightly larger.</li>
                </ol>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-14 bg-white border border-[#E8DCC4] rounded-sm p-7" data-testid="size-chart">
              <h2 className="font-display text-xl text-neutral-900">US ring size chart</h2>
              <p className="text-xs text-neutral-400 mt-1">Circles shown at relative scale for comparison — not true to size on screen.</p>
              <div className="mt-6 flex flex-wrap items-end justify-center gap-x-1.5 gap-y-5">
                {SIZES.map(([size, dia]) => (
                  <div key={size} className="flex flex-col items-center gap-2">
                    <div
                      className="rounded-full border-2 border-[#C9A227]/70 bg-[#FDFBF7]"
                      style={{ width: dia * MM, height: dia * MM }}
                    />
                    <span className="text-[11px] font-semibold text-neutral-700">{pretty(size)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 overflow-x-auto">
                <table className="w-full text-sm" data-testid="size-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-[0.15em] text-neutral-500 border-b border-[#E8DCC4]">
                      <th className="py-2 pr-4 font-semibold">US size</th>
                      <th className="py-2 pr-4 font-semibold">Inside diameter (mm)</th>
                      <th className="py-2 font-semibold">Circumference (mm)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SIZES.map(([size, dia, circ]) => (
                      <tr key={size} className="border-b border-[#F1EAD8] last:border-0">
                        <td className="py-2 pr-4 font-semibold text-neutral-900">{pretty(size)}</td>
                        <td className="py-2 pr-4 text-neutral-600">{dia.toFixed(1)}</td>
                        <td className="py-2 text-neutral-600">{circ.toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-10 bg-[#0D0D0D] rounded-sm p-7 text-neutral-300" data-testid="sizing-service-note">
              <h2 className="font-display text-xl text-white">Our ring sizing service</h2>
              <ul className="mt-4 space-y-2.5 text-sm leading-relaxed">
                <li>Every ring is sized before it ships — sizes 4 through 12.</li>
                <li>Sizing is <span className="text-[#E8CC6E] font-semibold">$25</span> for up to two sizes up, or any size down; each additional size up is <span className="text-[#E8CC6E] font-semibold">$15</span>.</li>
                <li>Outside sizes 4–12, or not sure?{" "}
                  <Link to="/contact?topic=Ring%20sizing" className="text-[#E8CC6E] font-semibold hover:text-[#C9A227] transition-colors duration-300">
                    Send us the details
                  </Link>{" "}
                  and we'll confirm what we can do.
                </li>
                <li>Please measure carefully — sized-to-order rings are{" "}
                  <Link to="/refund-policy" className="text-[#E8CC6E] font-semibold hover:text-[#C9A227] transition-colors duration-300">
                    non-refundable
                  </Link>.
                </li>
              </ul>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to="/catalog?category=Rings"
                  data-testid="ring-guide-shop"
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-7 py-3 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300"
                >
                  Shop rings <ArrowRight size={15} strokeWidth={1.5} />
                </Link>
                <Link
                  to="/collections/wedding-bands"
                  data-testid="ring-guide-wedding-bands"
                  className="inline-flex items-center gap-2 rounded-full border border-[#C9A227] text-[#E8CC6E] text-sm font-semibold px-7 py-3 hover:bg-[#C9A227]/10 transition-colors duration-300"
                >
                  Shop wedding bands <ArrowRight size={15} strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <Footer />
    </main>
  );
}
